import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import EmptyScreen from 'components/EmptyScreen';
import classNames from 'classnames';
import BuyerInfo from './BuyerInfo';
import { Button } from 'ncoded-component-library';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ListingInformation from './ListingInformation';
import SellerInfo from './SellerInfo';
import NdaSigningProgress from '../NdaSigningProgress';
import { getImgUrl } from 'components/ResolveImage/utils';
import PortfolioDataRoomNodes from '../../../../components/PortfolioDataRoomNodes';
import { Link } from 'react-router-dom';
import LinkToIcon from 'icons/LinkTo.icon';
import { checkHasListing, getSellingPortfolio } from 'utils';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { NewFilesEventData } from 'services/socketService/socketEventData';
import BuyerNdaCard from '../../../../components/BuyerNdaCard';
import { LeadNdaProps } from 'hooks/useNdaStatus';
import MessageFiles from 'components/MessageFiles';
import { NEW_WINDOW_PROPS } from 'data/global';

import './ContactPreview.styles.scss';
import './ContactPreview.styles.responsive.scss';

type ProfilePreviewProps = {
  className?: string;
};

const ProfilePreview: React.FC<ProfilePreviewProps> = (props) => {
  const { className } = props;

  const classes = classNames('contact-preview', className);

  const { t } = useTranslation();

  const {
    sessionId,
    currentSession,
    interlocutor,
    loadingSession,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    setContactOpen,
    setCurrentSession,
  } = useContext(MessagingContext);

  const {
    listing,
    leadWithdrawnInterest,
    isExternalCommunication,
    dataRoomNodes,
    enquiryQuestions,
    isBuyer,
    dealRoomOwnerName,
    leadId,
    signingStatus,
    signedDate,
    envelopeId,
    manuallySetNDAStatus,
    rejectionReason,
    isDirectMessage,
  } = currentSession || {};

  const isInterlocutorSeller = isBuyer;
  const showSellerPanel = !!listing?.dealRoom?.companyName;

  const RenderedMessageFiles = useMemo(
    () => (
      <MessageFiles
        sessionId={sessionId}
        dataRoomId={dataRoomId}
        dataRoomName={dataRoomName}
        ownerId={ownerId}
        userSide={userSide}
      />
    ),
    [dataRoomId, dataRoomName, ownerId, sessionId, userSide],
  );

  const updateNdaProps = useCallback(
    (leadNdaProps: LeadNdaProps) => {
      setCurrentSession((old) => ({
        ...old,
        ...leadNdaProps,
      }));
    },
    [setCurrentSession],
  );

  const newFiles = useCallback(
    ({ dataRoomId, dataRoomNodes }: NewFilesEventData) => {
      const currentDataRoomId = listing?.dataRoomId;

      if (dataRoomId !== currentDataRoomId) return;

      setCurrentSession((prev) => ({
        ...prev,
        dataRoomNodes,
      }));
    },
    [listing?.dataRoomId, setCurrentSession],
  );

  useSocketEvent(LISTEN_EVENTS.NEW_FILES, newFiles);

  if (loadingSession) return null;

  if (!interlocutor)
    return (
      <EmptyScreen
        className={classNames('contact-preview__empty-screen', className)}
        content={t('selectPersonToSeeProfile')}
      />
    );

  return (
    <div className={classes}>
      <div className="contact-preview__actions">
        <Button
          variant="icon"
          className="chat-arrow"
          icon={ArrowleftIcon}
          onClick={() => setContactOpen((old) => !old)}
          aria-label="Back to chat."
        />
      </div>

      {isDirectMessage ? (
        <>
          <BuyerInfo buyer={interlocutor} />
          {RenderedMessageFiles}
        </>
      ) : (
        <>
          {isInterlocutorSeller ? (
            showSellerPanel && (
              <SellerInfo
                title={listing?.dealRoom?.companyName}
                imageUrl={getImgUrl(listing?.dealRoom?.companyLogo?.url, '240')}
                leadWithdrawnInterest={leadWithdrawnInterest}
              />
            )
          ) : (
            <BuyerInfo
              buyer={interlocutor}
              enquiryQuestions={enquiryQuestions}
              leadWithdrawnInterest={leadWithdrawnInterest}
              isExternalCommunication={isExternalCommunication}
              dealRoomOwnerName={dealRoomOwnerName}
            />
          )}

          {!isInterlocutorSeller ? (
            <NdaSigningProgress />
          ) : (
            <BuyerNdaCard
              leadId={leadId}
              signingStatus={signingStatus}
              envelopeId={envelopeId}
              signedDate={signedDate}
              manuallySetNDAStatus={manuallySetNDAStatus}
              envelopeTemplateName={listing?.envelopeTemplateName}
              rejectionReason={rejectionReason}
              updateNdaProps={updateNdaProps}
            />
          )}

          {RenderedMessageFiles}

          {isInterlocutorSeller && (
            <PortfolioDataRoomNodes
              dataRoomId={listing?.dataRoomId}
              dataRoomNodes={dataRoomNodes}
              dataRoomName={dataRoomName}
              ownerId={ownerId}
              userSide={userSide}
            />
          )}

          {isInterlocutorSeller &&
            checkHasListing(listing?.dealRoom?.listingStage) && (
              <ListingInformation
                listing={listing}
                isInterlocutorSeller={isInterlocutorSeller}
                isExternalCommunication={isExternalCommunication}
              />
            )}

          {isExternalCommunication && (
            <div className="external-communicatator-info">
              <p>{listing?.dealRoom?.portfolioName}</p>
              <Link
                to={getSellingPortfolio(listing?.dealRoomId)}
                {...NEW_WINDOW_PROPS}
              >
                <LinkToIcon />
                {t('portfolio')}
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePreview;
